<script setup lang="ts">
// Imports
import type { StepEntity } from 'v-onboarding'
import { SolTemplateHeader } from '@solfacil/girassol'
import HeaderSearchProjects from '~/components/header/HeaderSearchProjects.vue'
import TopAlert from '~/components/shared/TopAlert.vue'
import ViewPartnerAlert from '~/components/shared/ViewPartnerAlert.vue'
import { menuHeader } from '~/composables/header'
import { useOnboardingHost } from '~/composables/onboarding/useOnboardingHost'
import useHeaderTabMenuItems from '~/composables/useHeaderTabMenuItems'
import useLayoutBase from '~/composables/useLayoutBase'
import useNotificationsManager from '~/composables/useNotificationsManager'
import usePartnerManager from '~/composables/usePartnerManager'
import useAuth from '~/store-v2/auth/useAuth'
import usePartner from '~/store-v2/partners/usePartner'
import { useOnboardingStore } from '~/store/onboarding'
import { useBlocklistAlert } from '~/ui-blocks/overview/Notifications/useBlocklistAlert'
import OverviewIntroModal from '~/ui-blocks/overview/OverviewIntroModal.vue'

// Composables/Hooks
const {
  impersonateStore,
  user,
  logout,
  product,
  partner,
  userId,
  termAlreadyAccepted,
} = useLayoutBase()

const {
  partnerName,
  partnerSelected,
  allPartners,
  isLoadingPartners,
  isFetchingPartners,
  handleChangeValue,
  selectPartner,
  handleImpersonate,
} = usePartnerManager(true)

const { notifications, handleClickNotification, notificationsError }
  = useNotificationsManager(userId)

const { data: auth } = useAuth()
const { t } = useI18n()

// Refs
const mainContentRef = ref<HTMLElement | null>(null)
const isHeaderVisible = ref(true)
const lastScrollTop = ref(0)
const headerRef = ref<InstanceType<typeof SolTemplateHeader> | null>(null)
const searchedValue = ref('')

const onboardingStore = useOnboardingStore()
const router = useRouter()
const route = useRoute()

const HEADER_TOUR_ID = 'layout-header'
const OVERVIEW_TOUR_ID = 'page-overview'

const INTRO_MODAL_HOME_VIEWED_KEY = 'intro_modal:home:viewed'
const INTRO_MODAL_INTERNAL_VIEWED_KEY = 'intro_modal:internal:viewed'
const INTRO_AUTO_START_ON_HOME_KEY = 'intro_modal:home:auto_start'
const INTRO_FLOW_KEY = 'intro_modal:flow'

const showOverviewIntroModal = ref(false)
const introModalMode = ref<'home' | 'internal'>('home')
function getCurrentFlow(): 'none' | 'home-full' | 'internal-full' | 'internal-header' {
  return (sessionStorage.getItem(INTRO_FLOW_KEY) as any) || 'none'
}

function setCurrentFlow(value: 'none' | 'home-full' | 'internal-full' | 'internal-header') {
  if (value === 'none')
    sessionStorage.removeItem(INTRO_FLOW_KEY)
  else
    sessionStorage.setItem(INTRO_FLOW_KEY, value)
}
let stopWaitOverviewTourRegistration: (() => void) | null = null

const {
  AsyncOnboardingWrapper,
  onboardingWrapperRef,
  shouldRenderOnboarding,
  activeSteps,
  activeOptions,
  handleOnboardingFinish: handleOnboardingFinishInternal,
} = useOnboardingHost(mainContentRef)

const headerFinishLabel = computed(() => {
  if (onboardingStore.activeTourId !== HEADER_TOUR_ID)
    return undefined

  if (isHomeRoute())
    return t('onboarding.layout.header.finish_home')

  return t('onboarding.layout.header.finish_internal')
})
function handleOnboardingFinish() {
  handleOnboardingFinishInternal()

  if (!onboardingStore.isActive) {
    const headerDone = onboardingStore.isTourCompleted(HEADER_TOUR_ID)
    const overviewDone = onboardingStore.isTourCompleted(OVERVIEW_TOUR_ID)
    const flow = getCurrentFlow()

    if (flow === 'home-full' && headerDone && overviewDone) {
      localStorage.setItem(INTRO_MODAL_HOME_VIEWED_KEY, 'true')
      localStorage.setItem(INTRO_MODAL_INTERNAL_VIEWED_KEY, 'true')
      setCurrentFlow('none')
    }

    if (flow === 'internal-full' && headerDone && overviewDone) {
      localStorage.setItem(INTRO_MODAL_HOME_VIEWED_KEY, 'true')
      localStorage.setItem(INTRO_MODAL_INTERNAL_VIEWED_KEY, 'true')
      setCurrentFlow('none')
    }

    if (flow === 'internal-header' && headerDone) {
      localStorage.setItem(INTRO_MODAL_INTERNAL_VIEWED_KEY, 'true')
      setCurrentFlow('none')
    }
  }

  syncIntroModalVisibility()
}

const headerTourSteps = ([
  {
    attachTo: { element: '#layout-header-anchor' },
    content: {
      title: t('onboarding.layout.header.step1.title'),
      description: t('onboarding.layout.header.step1.description'),
    },
    options: {
      scrollToStep: {
        enabled: true,
        options: { block: 'start', inline: 'nearest', behavior: 'auto' } as const,
      },
    },
  },
] satisfies StepEntity[])

onboardingStore.registerTour({
  id: 'layout-header',
  steps: headerTourSteps,
  priority: 0,
  storageKey: 'onboarding:layout-header:completed',
})

function closeIntroModal() {
  showOverviewIntroModal.value = false
}

function requestHeaderTourIfNeeded() {
  onboardingStore.requestTour(HEADER_TOUR_ID)
}

function startFullTourFlow() {
  onboardingStore.requestTour(HEADER_TOUR_ID)

  stopWaitOverviewTourRegistration?.()
  stopWaitOverviewTourRegistration = watch(
    () => Boolean(onboardingStore.tours[OVERVIEW_TOUR_ID]),
    (isRegistered) => {
      if (!isRegistered) return
      onboardingStore.requestTour(OVERVIEW_TOUR_ID)
      stopWaitOverviewTourRegistration?.()
      stopWaitOverviewTourRegistration = null
    },
    { immediate: true },
  )
}

function isHomeRoute() {
  return route.path === '/'
}

function syncIntroModalVisibility() {
  if (onboardingStore.isActive) {
    closeIntroModal()
    return
  }

  if (getCurrentFlow() !== 'none') {
    closeIntroModal()
    return
  }

  const homeViewed = Boolean(localStorage.getItem(INTRO_MODAL_HOME_VIEWED_KEY))
  const internalViewed = Boolean(localStorage.getItem(INTRO_MODAL_INTERNAL_VIEWED_KEY))
  const autoStartOnHome = Boolean(sessionStorage.getItem(INTRO_AUTO_START_ON_HOME_KEY))

  if (isHomeRoute()) {
    if (!homeViewed && !autoStartOnHome) {
      introModalMode.value = 'home'
      showOverviewIntroModal.value = true
      return
    }
  }
  else {
    if (!homeViewed && !internalViewed) {
      introModalMode.value = 'internal'
      showOverviewIntroModal.value = true
      return
    }
  }

  closeIntroModal()
}

async function handleIntroPrimary() {
  if (introModalMode.value === 'home') {
    setCurrentFlow('home-full')
    closeIntroModal()
    startFullTourFlow()
    return
  }

  setCurrentFlow('internal-full')
  closeIntroModal()
  await router.push('/')
}

function handleIntroSecondary() {
  if (introModalMode.value === 'home') {
    localStorage.setItem(INTRO_MODAL_HOME_VIEWED_KEY, 'true')
    closeIntroModal()
    requestHeaderTourIfNeeded()
    return
  }

  setCurrentFlow('internal-header')
  closeIntroModal()
  requestHeaderTourIfNeeded()
}

function handleIntroTertiary() {
  localStorage.setItem(INTRO_MODAL_INTERNAL_VIEWED_KEY, 'true')
  setCurrentFlow('none')
  closeIntroModal()
}

function handleIntroClose() {
  if (introModalMode.value === 'home') {
    handleIntroSecondary()
    return
  }
  handleIntroTertiary()
}

// Computed
const basePartnerIdForAlert = computed(() => {
  const perfil = auth.value?.me?.perfil
  if (perfil === 'admin' && impersonateStore.partnerId > 0) {
    return impersonateStore.partnerId.toString()
  }
  return auth.value?.person?.partner?.id?.toString() || ''
})

const { data: partnerDataForAlert } = usePartner(basePartnerIdForAlert)

const {
  userIsOnBlockedList,
} = useBlocklistAlert(partnerDataForAlert, basePartnerIdForAlert)

const hasSystemInstabilityAlert = computed(() => true)

const topAlertHasActive = computed(() => {
  return userIsOnBlockedList.value || hasSystemInstabilityAlert.value
})

const isAdmin = computed(() => user.value?.profile === 'admin')

const { tabMenuItems } = useHeaderTabMenuItems()

const searchPlaceholder = computed(() => t('header.search.placeholder'))

const menuHeaderInstance = menuHeader()

// Functions
function handleScroll() {
  if (!mainContentRef.value)
    return

  if (searchedValue.value && searchedValue.value.trim().length > 0) {
    isHeaderVisible.value = true
    lastScrollTop.value = mainContentRef.value.scrollTop
    return
  }

  const currentScrollTop = mainContentRef.value.scrollTop

  if (currentScrollTop <= 0) {
    isHeaderVisible.value = true
    lastScrollTop.value = 0
    return
  }

  if (currentScrollTop > lastScrollTop.value && currentScrollTop > 50) {
    isHeaderVisible.value = false
  }
  else if (currentScrollTop < lastScrollTop.value) {
    isHeaderVisible.value = true
  }

  lastScrollTop.value = currentScrollTop
}

function handleSearch(value: string) {
  searchedValue.value = value
}

// Lifecycle hooks
onMounted(() => {
  syncIntroModalVisibility()
  if (mainContentRef.value) {
    lastScrollTop.value = mainContentRef.value.scrollTop
    mainContentRef.value.addEventListener('scroll', handleScroll, { passive: true })
  }
})

watch(
  () => [route.path, onboardingStore.isActive],
  () => {
    const flow = getCurrentFlow()

    if (flow === 'home-full' && route.path === '/') {
      startFullTourFlow()
    }

    if (flow === 'internal-full' && route.path === '/') {
      startFullTourFlow()
    }

    if (flow === 'internal-header') {
      requestHeaderTourIfNeeded()
    }

    syncIntroModalVisibility()
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  if (mainContentRef.value) {
    mainContentRef.value.removeEventListener('scroll', handleScroll)
  }

  onboardingStore.unregisterTour('layout-header')
  stopWaitOverviewTourRegistration?.()
  stopWaitOverviewTourRegistration = null
})
</script>

<template>
  <div class="layout-container">
    <div class="alerts-wrapper">
      <ViewPartnerAlert v-if="impersonateStore.showAlertImpersonateActive" />
      <TopAlert
        v-if="topAlertHasActive"
      />
    </div>
    <main
      ref="mainContentRef"
      class="default-layout"
    >
      <div
        id="layout-header-anchor"
        class="header-wrapper"
        :class="{ 'header-hidden': !isHeaderVisible }"
      >
        <SolTemplateHeader
          ref="headerRef"
          :key="`header-${userId || 'default'}-${impersonateStore.partnerId || 'no-impersonate'}`"
          :user="user"
          :partner="partner"
          :product="product"
          :um-personification="impersonateStore.verifyIsImpersonated()"
          :in-personification="!impersonateStore.verifyIsImpersonated()"
          :view-ao="termAlreadyAccepted"
          :show-tab-menu="true"
          :tab-menu-items="tabMenuItems"
          :show-notification="true"
          :notifications="notifications"
          :notification-error="notificationsError"
          :search-placeholder="searchPlaceholder"
          @click-notification="handleClickNotification"
          @click-logout="logout"
          @click-user="menuHeaderInstance.goMenuUser"
          @search="handleSearch"
        >
          <template #search-results="{ searchValue: searchVal, loading }">
            <HeaderSearchProjects
              :header="headerRef"
              :search-value="searchVal"
              :searched-value="searchedValue"
              :loading="loading"
              :partner-id="isAdmin ? '' : partner.partnerId"
            />
          </template>
        </SolTemplateHeader>
      </div>
      <div class="main-content">
        <RouterView class="w-full" />
      </div>
      <ImpersonateModal
        :loading="isLoadingPartners || isFetchingPartners"
        :partners="allPartners"
        :partner-id="partnerSelected?.partnerId"
        :partner-name="partnerName"
        @handle-change-value="handleChangeValue"
        @select-partner="selectPartner"
        @handle-impersonate="handleImpersonate"
      />

      <component
        :is="AsyncOnboardingWrapper"
        v-if="shouldRenderOnboarding"
        ref="onboardingWrapperRef"
        :steps="activeSteps"
        :options="activeOptions"
        :finish-label="headerFinishLabel"
        @finish="handleOnboardingFinish"
      />

      <OverviewIntroModal
        :is-open="showOverviewIntroModal"
        :mode="introModalMode"
        @primary="handleIntroPrimary"
        @secondary="handleIntroSecondary"
        @tertiary="handleIntroTertiary"
        @close="handleIntroClose"
      />
    </main>
  </div>
</template>

<style lang="scss" scoped>
.layout-container {
  @apply flex flex-col h-screen overflow-hidden;
}

.alerts-wrapper {
  @apply flex flex-col flex-shrink-0;
}

.default-layout {
  @apply flex flex-col flex-1 min-h-0 overflow-y-auto overflow-x-hidden;
  @apply bg-neutral-high-light;

  .header-wrapper {
    @apply sticky top-0 z-19;
    transition: transform 0.3s ease-in-out;

    &.header-hidden {
      transform: translateY(-100%);
    }
  }

  .main-content {
    @apply max-w-[1440px];
    @apply mx-auto w-full;
  }
}
</style>
